// Reprise d'une location déjà en cours (F-005) : assistant en cinq étapes et suivi de la reprise.
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { post, upload, type FieldError as FieldErrorShape } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ErrorBox,
  Fil,
  FileDrop,
  FileLine,
  Input,
  KeyValues,
  Loading,
  MoneyInput,
  Notice,
  PageHeader,
  Select,
  StatusChip,
  Textarea,
  fieldError,
  useToast,
} from "@core/ui";
import { numOrNull, type Property } from "./shared";

type Party = { full_name: string; email: string; phone: string; share: string };
const EMPTY_PARTY: Party = { full_name: "", email: "", phone: "", share: "" };

const REGIMES = [
  { value: "vide", label: "Location vide" },
  { value: "meuble", label: "Location meublée" },
  { value: "colocation", label: "Colocation" },
  { value: "mobilite", label: "Bail mobilité" },
  { value: "etudiant", label: "Bail étudiant (meublé)" },
];

const DEPOSIT_STATUS = [
  { value: "encaisse", label: "Encaissé en totalité" },
  { value: "partiel", label: "Encaissé en partie" },
  { value: "non_encaisse", label: "Prévu au bail mais pas encore encaissé" },
  { value: "aucun", label: "Aucun dépôt prévu" },
];

function monthInput(offset: number): string {
  const d = new Date();
  const total = d.getFullYear() * 12 + d.getMonth() + offset;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
}

function monthLabel(v: string): string {
  if (!/^\d{4}-\d{2}$/.test(v)) return v || "—";
  return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date(`${v}-15T12:00:00`));
}

const STEP_TITLES = ["Logement et bail", "Locataires et garants", "Loyer, charges et dépôt", "Reprise des échéances", "Vérification et confirmation"];

// Champs de l'API renvoyés en erreur, regroupés par étape pour y ramener l'utilisateur.
const FIELD_STEP: Record<string, number> = {
  property_id: 0,
  regime: 0,
  start_on: 0,
  end_on: 0,
  duration_months: 0,
  tenants: 1,
  rent_cents: 2,
  charges_cents: 2,
  charges_mode: 2,
  deposit_cents: 2,
  deposit_status: 2,
  deposit_held_cents: 2,
  payment_day: 2,
  "revision.indice": 3,
  "revision.trimestre": 3,
  "revision.annee": 3,
  managed_from: 3,
  last_receipted_period: 3,
  opening_balance_note: 3,
  confirmations: 4,
};

export function ImportLeasePage() {
  const org = useOrg();
  const nav = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [params] = useSearchParams();
  const props = useGet<{ items: Property[] }>(org.api("/properties"));
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    property_id: params.get("logement") ?? "",
    regime: "vide",
    start_on: "",
    end_on: "",
    duration_months: "",
    rent_cents: null as number | null,
    charges_cents: null as number | null,
    charges_mode: "provision",
    deposit_cents: null as number | null,
    deposit_status: "encaisse",
    deposit_held_cents: null as number | null,
    payment_day: "1",
    tenants: [{ ...EMPTY_PARTY }] as Party[],
    guarantors: [] as Party[],
    revision_indice: "irl",
    revision_trimestre: "",
    revision_annee: "",
    revision_valeur: "",
    revision_date_anniversaire: "",
    revision_derniere: "",
    revision_a_examiner: false,
    managed_from: monthInput(1),
    last_receipted_period: monthInput(0),
    opening_balance_cents: null as number | null,
    opening_balance_sign: "du",
    opening_balance_note: "",
    notes: "",
    confirm_montants: false,
    confirm_dates: false,
    confirm_solde: false,
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const err = (k: string) => fieldError(error, k);
  const candidates = (props.data?.items ?? []).filter((p) => ["logement", "chambre", "cave", "parking", "autre"].includes(p.kind) && !p.archived_at);
  const property = candidates.find((p) => p.id === f.property_id);
  const colocation = f.regime === "colocation";
  const openingCents = (f.opening_balance_cents ?? 0) * (f.opening_balance_sign === "trop_percu" ? -1 : 1);

  const body = () => ({
    property_id: f.property_id || null,
    regime: f.regime,
    start_on: f.start_on,
    end_on: f.end_on || null,
    duration_months: f.duration_months ? Number(f.duration_months) : null,
    rent_cents: f.rent_cents ?? 0,
    charges_cents: f.charges_cents ?? 0,
    charges_mode: f.charges_mode,
    deposit_cents: f.deposit_cents ?? 0,
    deposit_status: f.deposit_status,
    deposit_held_cents: f.deposit_status === "partiel" ? f.deposit_held_cents : null,
    payment_day: Number(f.payment_day) || 0,
    tenants: f.tenants.map((t) => ({ full_name: t.full_name, email: t.email || null, phone: t.phone || null, share_pct: colocation && t.share ? numOrNull(t.share) : null })),
    guarantors: f.guarantors.filter((g) => g.full_name.trim()).map((g) => ({ full_name: g.full_name, email: g.email || null, phone: g.phone || null })),
    revision:
      f.revision_indice === "irl"
        ? {
            indice: "irl",
            trimestre: f.revision_trimestre ? Number(f.revision_trimestre) : null,
            annee: f.revision_annee ? Number(f.revision_annee) : null,
            valeur: f.revision_valeur || null,
            date_anniversaire: f.revision_date_anniversaire || null,
            derniere_revision: f.revision_derniere || null,
            a_examiner: f.revision_a_examiner,
          }
        : { indice: "aucune" },
    managed_from: f.managed_from,
    last_receipted_period: f.last_receipted_period || null,
    opening_balance_cents: openingCents,
    opening_balance_note: f.opening_balance_note,
    confirmations: { montants: f.confirm_montants, dates: f.confirm_dates, solde: f.confirm_solde },
    notes: f.notes,
  });

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await post<{ id: string }>(org.api("/lease-imports"), body());
      invalidate(org.api("/properties"), org.api("/lease-imports"), org.api("/patrimoine"));
      toast("Location reprise. Aucun appel de loyer n’a été créé pour la période passée.");
      nav(org.to(`logements/reprises/${res.id}`));
    } catch (e: any) {
      setError(e);
      const fields: FieldErrorShape[] = e?.fields ?? [];
      const steps = fields.map((x) => FIELD_STEP[x.field]).filter((x) => x !== undefined);
      if (steps.length) setStep(Math.min(...steps));
    } finally {
      setBusy(false);
    }
  }

  const partyRows = (list: Party[], key: "tenants" | "guarantors", label: string) => (
    <div className="stack">
      {list.map((p, i) => (
        <fieldset key={i} className="pat-fieldset">
          <legend>
            {label} {i + 1}
          </legend>
          <div className="form-row">
            <Input label="Nom et prénom" required value={p.full_name} onChange={(e) => set(key, list.map((x, j) => (j === i ? { ...x, full_name: e.target.value } : x)))} />
            <Input label="Adresse e-mail" type="email" value={p.email} onChange={(e) => set(key, list.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
            <Input label="Téléphone" type="tel" value={p.phone} onChange={(e) => set(key, list.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
            {key === "tenants" && colocation ? (
              <Input label="Part du loyer (%)" inputMode="decimal" help="Facultatif ; toutes ou aucune." value={p.share} onChange={(e) => set(key, list.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))} />
            ) : null}
          </div>
          {list.length > (key === "tenants" ? 1 : 0) ? (
            <div className="form-actions">
              <Button small variant="discret" onClick={() => set(key, list.filter((_, j) => j !== i))}>
                Retirer
              </Button>
            </div>
          ) : null}
        </fieldset>
      ))}
      <div>
        <Button small variant="secondaire" onClick={() => set(key, [...list, { ...EMPTY_PARTY }])}>
          Ajouter {key === "tenants" ? "un locataire" : "un garant"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="page">
      <PageHeader
        eyebrow="Location"
        title="Reprendre une location en cours"
        intro="Saisissez le bail déjà signé. Vous confirmez les montants, les dates et le solde repris ; Bailivo ne recalcule pas le passé et ne relance pas les périodes déjà réglées."
      />
      <Notice>
        L’historique repris ne vaut pas vérification du dossier du locataire. Les échéances gérées par Bailivo commencent au mois que vous choisissez ; tout ce qui précède est résumé dans le solde d’ouverture.
      </Notice>
      <Fil steps={STEP_TITLES.map((t, i) => ({ title: t, state: i < step ? "fait" : i === step ? "actuel" : "a-venir" }))} />
      <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
      {(error as any)?.fields?.length ? (
        <Notice tone="erreur" title="À corriger avant de reprendre la location">
          <ul className="pat-list">
            {((error as any).fields as FieldErrorShape[]).map((x) => (
              <li key={x.field + x.message}>{x.message}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      <Card title={`${step + 1}. ${STEP_TITLES[step]}`}>
        {step === 0 ? (
          <div className="stack">
            {props.isLoading ? <Loading /> : null}
            <Select
              label="Bien loué"
              required
              value={f.property_id}
              error={err("property_id")}
              options={[{ value: "", label: "Choisissez le bien" }, ...candidates.map((p) => ({ value: p.id, label: `${p.label}${p.parent_label ? ` (dans ${p.parent_label})` : ""} — ${p.occupation?.label ?? ""}` }))]}
              onChange={(e) => set("property_id", e.target.value)}
            />
            {property && property.occupation?.status !== "vacant" ? (
              <Notice tone="attention">Ce bien apparaît déjà loué ({property.occupation?.label}). Un même espace ne peut pas être loué deux fois.</Notice>
            ) : null}
            {candidates.length === 0 && !props.isLoading ? (
              <p>
                Aucun logement. <Link to={org.to("logements")}>Ajoutez d’abord le logement</Link>.
              </p>
            ) : null}
            <Select label="Type de bail" value={f.regime} options={REGIMES} error={err("regime")} help="Les baux commerciaux et professionnels ont une entrée dédiée." onChange={(e) => set("regime", e.target.value)} />
            <div className="form-row">
              <Input label="Date de prise d’effet" type="date" required value={f.start_on} error={err("start_on")} onChange={(e) => set("start_on", e.target.value)} />
              <Input label={f.regime === "mobilite" ? "Date de fin (obligatoire)" : "Date de fin (facultatif)"} type="date" value={f.end_on} error={err("end_on")} onChange={(e) => set("end_on", e.target.value)} />
              <Input label="Durée (mois)" inputMode="numeric" value={f.duration_months} error={err("duration_months")} onChange={(e) => set("duration_months", e.target.value)} />
            </div>
          </div>
        ) : null}
        {step === 1 ? (
          <div className="stack">
            {fieldError(error, "tenants") ? <Notice tone="erreur">{fieldError(error, "tenants")}</Notice> : null}
            {colocation ? <p className="small muted">Colocation : ajoutez chaque colocataire. Pour une colocation par chambres, reprenez plutôt un bail par chambre.</p> : null}
            {partyRows(f.tenants, "tenants", "Locataire")}
            <h3>Garants</h3>
            {f.guarantors.length === 0 ? <p className="small muted">Aucun garant saisi.</p> : null}
            {partyRows(f.guarantors, "guarantors", "Garant")}
          </div>
        ) : null}
        {step === 2 ? (
          <div className="stack">
            <div className="form-row">
              <MoneyInput label="Loyer hors charges en vigueur" required cents={f.rent_cents} error={err("rent_cents")} onChange={(c) => set("rent_cents", c)} />
              <MoneyInput label="Charges" cents={f.charges_cents} error={err("charges_cents")} onChange={(c) => set("charges_cents", c)} />
              <Select
                label="Charges"
                value={f.charges_mode}
                options={[
                  { value: "provision", label: "Provision (régularisée)" },
                  { value: "forfait", label: "Forfait" },
                ]}
                onChange={(e) => set("charges_mode", e.target.value)}
              />
              <Input label="Jour de paiement" inputMode="numeric" value={f.payment_day} error={err("payment_day")} help="Entre 1 et 28." onChange={(e) => set("payment_day", e.target.value)} />
            </div>
            <div className="form-row">
              <MoneyInput label="Dépôt de garantie prévu au bail" cents={f.deposit_cents} error={err("deposit_cents")} onChange={(c) => set("deposit_cents", c)} />
              <Select label="Dépôt de garantie" value={f.deposit_status} options={DEPOSIT_STATUS} error={err("deposit_status")} onChange={(e) => set("deposit_status", e.target.value)} />
              {f.deposit_status === "partiel" ? (
                <MoneyInput label="Somme réellement encaissée" cents={f.deposit_held_cents} error={err("deposit_held_cents")} onChange={(c) => set("deposit_held_cents", c)} />
              ) : null}
            </div>
          </div>
        ) : null}
        {step === 3 ? (
          <div className="stack">
            <fieldset className="pat-fieldset">
              <legend>Révision du loyer</legend>
              <div className="form-row">
                <Select
                  label="Clause de révision"
                  value={f.revision_indice}
                  options={[
                    { value: "irl", label: "Indice de référence des loyers (IRL)" },
                    { value: "aucune", label: "Aucune révision prévue" },
                  ]}
                  error={err("revision.indice")}
                  onChange={(e) => set("revision_indice", e.target.value)}
                />
                {f.revision_indice === "irl" ? (
                  <>
                    <Select
                      label="Trimestre de référence"
                      value={f.revision_trimestre}
                      error={err("revision.trimestre")}
                      options={[{ value: "", label: "Choisissez" }, ...[1, 2, 3, 4].map((t) => ({ value: String(t), label: `${t}${t === 1 ? "er" : "e"} trimestre` }))]}
                      onChange={(e) => set("revision_trimestre", e.target.value)}
                    />
                    <Input label="Année de l’indice" inputMode="numeric" value={f.revision_annee} error={err("revision.annee")} onChange={(e) => set("revision_annee", e.target.value)} />
                    <Input label="Valeur de l’indice au bail" value={f.revision_valeur} placeholder="Ex. 145,78" onChange={(e) => set("revision_valeur", e.target.value)} />
                  </>
                ) : null}
              </div>
              {f.revision_indice === "irl" ? (
                <>
                  <div className="form-row">
                    <Input label="Date anniversaire (JJ/MM)" value={f.revision_date_anniversaire} onChange={(e) => set("revision_date_anniversaire", e.target.value)} />
                    <Input label="Dernière révision appliquée" type="date" value={f.revision_derniere} onChange={(e) => set("revision_derniere", e.target.value)} />
                  </div>
                  <Checkbox label="Une révision est en cours ou reste à appliquer" help="Bailivo ne calcule pas de nouveau loyer : la révision sera signalée pour examen." checked={f.revision_a_examiner} onChange={(v) => set("revision_a_examiner", v)} />
                </>
              ) : null}
            </fieldset>
            <fieldset className="pat-fieldset">
              <legend>Point de départ dans Bailivo</legend>
              <div className="form-row">
                <Input label="Dernière période déjà quittancée" type="month" value={f.last_receipted_period} error={err("last_receipted_period")} help="Hors Bailivo. Laissez vide si inconnue." onChange={(e) => set("last_receipted_period", e.target.value)} />
                <Input label="Premier mois géré par Bailivo" type="month" required value={f.managed_from} error={err("managed_from")} onChange={(e) => set("managed_from", e.target.value)} />
              </div>
              <div className="form-row">
                <MoneyInput label="Solde d’ouverture" cents={f.opening_balance_cents} help="Laissez vide si le locataire est à jour." onChange={(c) => set("opening_balance_cents", c)} />
                <Select
                  label="Sens du solde"
                  value={f.opening_balance_sign}
                  options={[
                    { value: "du", label: "Dû par le locataire" },
                    { value: "trop_percu", label: "Trop-perçu à rendre au locataire" },
                  ]}
                  onChange={(e) => set("opening_balance_sign", e.target.value)}
                />
              </div>
              <Input
                label="Justification du solde"
                placeholder="Ex. loyer d’août non réglé, 540,00 €"
                value={f.opening_balance_note}
                error={err("opening_balance_note")}
                onChange={(e) => set("opening_balance_note", e.target.value)}
              />
            </fieldset>
            <Textarea label="Notes sur la reprise" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        ) : null}
        {step === 4 ? (
          <div className="stack">
            <div className="pat-recap">
              <KeyValues
                items={[
                  ["Bien", property?.label ?? "—"],
                  ["Type de bail", REGIMES.find((r) => r.value === f.regime)?.label ?? f.regime],
                  ["Prise d’effet", f.start_on ? date(f.start_on) : "—"],
                  ["Fin", f.end_on ? date(f.end_on) : "—"],
                  ["Locataires", f.tenants.map((t) => t.full_name + (colocation && t.share ? ` (${t.share} %)` : "")).filter(Boolean).join(", ") || "—"],
                  ["Garants", f.guarantors.map((g) => g.full_name).filter(Boolean).join(", ") || "—"],
                  ["Loyer hors charges", money(f.rent_cents ?? 0)],
                  ["Charges", `${money(f.charges_cents ?? 0)} (${f.charges_mode === "forfait" ? "forfait" : "provision"})`],
                  ["Jour de paiement", f.payment_day],
                  ["Dépôt de garantie", `${money(f.deposit_cents ?? 0)} · ${DEPOSIT_STATUS.find((d) => d.value === f.deposit_status)?.label.toLowerCase()}${f.deposit_status === "partiel" ? ` (${money(f.deposit_held_cents ?? 0)} encaissés)` : ""}`],
                  ["Révision", f.revision_indice === "irl" ? `IRL ${f.revision_trimestre ? `T${f.revision_trimestre}` : ""} ${f.revision_annee}${f.revision_a_examiner ? " · à examiner" : ""}` : "Aucune"],
                  ["Dernière quittance", monthLabel(f.last_receipted_period)],
                  ["Premier mois géré", monthLabel(f.managed_from)],
                  ["Solde d’ouverture", openingCents === 0 ? "Aucun" : openingCents > 0 ? `${money(openingCents)} dû par le locataire` : `${money(-openingCents)} de trop-perçu`],
                ]}
              />
            </div>
            <fieldset className="pat-fieldset">
              <legend>Confirmation</legend>
              <Checkbox label="Je confirme les montants repris (loyer, charges, dépôt)." checked={f.confirm_montants} onChange={(v) => set("confirm_montants", v)} />
              <Checkbox label="Je confirme les dates (prise d’effet, fin, premier mois géré)." checked={f.confirm_dates} onChange={(v) => set("confirm_dates", v)} />
              <Checkbox label="Je confirme le solde d’ouverture et sa justification." checked={f.confirm_solde} onChange={(v) => set("confirm_solde", v)} />
              {err("confirmations") ? <p className="bv-champ-aide">{err("confirmations")}</p> : null}
            </fieldset>
          </div>
        ) : null}
        <div className="form-actions">
          {step > 0 ? (
            <Button variant="discret" onClick={() => setStep(step - 1)}>
              Étape précédente
            </Button>
          ) : (
            <Link className="bv-btn bv-btn-discret" to={org.to("logements")}>
              Annuler
            </Link>
          )}
          {step < STEP_TITLES.length - 1 ? (
            <Button onClick={() => setStep(step + 1)}>Étape suivante</Button>
          ) : (
            <Button busy={busy} disabled={!(f.confirm_montants && f.confirm_dates && f.confirm_solde)} onClick={submit}>
              Reprendre la location
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suivi d'une reprise

type ImportDetail = {
  id: string;
  lease_id: string;
  property_id: string;
  property_label: string;
  regime_label: string;
  lease_status: string;
  start_on: string;
  end_on: string | null;
  rent_cents: number;
  charges_cents: number;
  charges_mode: string;
  deposit_cents: number;
  deposit_status: string;
  deposit_held_cents: number;
  payment_day: number;
  revision: Record<string, any>;
  managed_from_label: string;
  last_receipted_label: string | null;
  opening_balance_cents: number;
  opening_balance_note: string;
  warnings: string[];
  notes: string;
  confirmed_by: string | null;
  confirmed_at: string;
  parties: { role: string; full_name: string; email: string | null; share_label: string | null }[];
  documents: { id: string; kind: string; title: string; filename: string; size_bytes: number; url: string }[];
  checklist: { code: string; label: string; done: boolean; kind: string }[];
  document_kinds: { kind: string; label: string }[];
  provenance: string;
  rent_calls_note: string;
};

const ROLE: Record<string, string> = { locataire: "Locataire", garant: "Garant", bailleur: "Bailleur", mandataire: "Mandataire" };

export function ImportDetailPage() {
  const org = useOrg();
  const { importId = "" } = useParams();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<ImportDetail>(org.api(`/lease-imports/${importId}`));
  const [kind, setKind] = useState("bail_signe");
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Location reprise"
        title={d.property_label}
        intro={`${d.regime_label} depuis le ${date(d.start_on)}. Reprise confirmée le ${dateTime(d.confirmed_at)}${d.confirmed_by ? ` par ${d.confirmed_by}` : ""}.`}
        actions={
          <>
            <Link className="bv-btn bv-btn-secondaire" to={org.to(`logements/${d.property_id}`)}>
              Voir le logement
            </Link>
            <Link className="bv-btn bv-btn-primaire" to={org.to(`baux/${d.lease_id}`)}>
              Ouvrir le bail
            </Link>
          </>
        }
      />
      <Notice>{d.provenance}</Notice>
      <Notice tone="info">{d.rent_calls_note}</Notice>
      {d.warnings.map((w) => (
        <Notice key={w} tone="attention">
          {w}
        </Notice>
      ))}
      <div className="grid-2">
        <Card title="Bail repris">
          <KeyValues
            items={[
              ["Loyer hors charges", money(d.rent_cents)],
              ["Charges", `${money(d.charges_cents)} (${d.charges_mode === "forfait" ? "forfait" : "provision"})`],
              ["Jour de paiement", d.payment_day],
              ["Dépôt prévu", money(d.deposit_cents)],
              ["Dépôt encaissé", money(d.deposit_held_cents)],
              ["Fin", d.end_on ? date(d.end_on) : "—"],
              ["Révision", d.revision?.indice === "irl" ? `IRL T${d.revision.trimestre ?? "?"} ${d.revision.annee ?? ""}${d.revision.valeur ? ` (${d.revision.valeur})` : ""}` : "Aucune"],
              ["Dernière quittance hors Bailivo", d.last_receipted_label ?? "Non renseignée"],
              ["Premier mois géré", d.managed_from_label],
              ["Solde d’ouverture", d.opening_balance_cents === 0 ? "Aucun" : `${money(Math.abs(d.opening_balance_cents))} ${d.opening_balance_cents > 0 ? "dû par le locataire" : "de trop-perçu"}`],
              ["Justification", d.opening_balance_note || "—"],
            ]}
          />
        </Card>
        <Card title="Parties">
          <ul className="list-plain">
            {d.parties.map((p, i) => (
              <li key={i}>
                <Badge>{ROLE[p.role] ?? p.role}</Badge> {p.full_name}
                {p.share_label ? <span className="muted small"> · {p.share_label}</span> : null}
                {p.email ? <span className="pat-sub">{p.email}</span> : null}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Card title="À compléter">
        <ul className="list-plain">
          {d.checklist.map((c) => (
            <li key={c.code} className="row">
              <StatusChip status={c.done ? "conforme" : c.kind === "piece" ? "manquant" : "a_confirmer"} label={c.done ? "Fait" : c.kind === "piece" ? "Pièce manquante" : "À traiter"} />
              <span>{c.label}</span>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Pièces de la location">
        {d.documents.map((doc) => (
          <FileLine key={doc.id} name={doc.title || doc.filename} size={doc.size_bytes} href={doc.url} />
        ))}
        {org.can("operate") ? (
          <div className="stack-sm">
            <Select label="Type de pièce" value={kind} options={d.document_kinds.map((k) => ({ value: k.kind, label: k.label }))} onChange={(e) => setKind(e.target.value)} />
            <FileDrop
              busy={busy}
              onFiles={async (files) => {
                if (!files[0]) return;
                setBusy(true);
                try {
                  const form = new FormData();
                  form.append("kind", kind);
                  form.append("file", files[0]);
                  await upload(org.api(`/lease-imports/${d.id}/documents`), form);
                  invalidate(org.api(`/lease-imports/${d.id}`));
                  toast("Pièce ajoutée.");
                } catch (e: any) {
                  toast(e.message, "erreur");
                } finally {
                  setBusy(false);
                }
              }}
            />
          </div>
        ) : null}
      </Card>
      {d.notes ? (
        <Card title="Notes">
          <p>{d.notes}</p>
        </Card>
      ) : null}
    </div>
  );
}
